import { string } from 'prop-types';
import { ErrorType } from '../models/HttpError';
import { IResult } from '../models/HttpResult';
import { IConsumptionsFilters } from '../models/Filters/ConsumptionsFilters';
import { ContractConcept } from '../models/Entities/Contracts/ContractConcept';
import { ProtocolPatient } from '../models/Entities/Protocols/ProtocolPatient';
import { Practitioner } from '../models/Entities/Practitioners/Practitioner';
import { ContractStage } from '../models/Entities/Contracts/ContractStage';
import { IConsumptionStatusProps } from '../commons/component/Inputs/ProtocolConsumptionStatusSelector/ProtocolConsumptionStatusSelector.Functions';
import { Protocol } from '../models/Entities/Protocols/Protocol';

// return query param object
export const GetQueryStringParams = (
  pageNumber?: number,
  pageSize?: number,
  searchText?: string,
  filters: IConsumptionsFilters | undefined = undefined,
): URLSearchParams => {
  let paramsResult: URLSearchParams = new URLSearchParams();

  let paramsCollection: string[][] = [];

  if (pageNumber && !pageSize && !searchText) {
    paramsResult = new URLSearchParams({
      PageNumber: String(pageNumber)
    });
  }

  if (pageSize && !pageNumber && !searchText) {
    paramsResult = new URLSearchParams({
      PageSize: String(pageSize)
    });
  }

  if (searchText && !pageNumber && !pageSize) {
    paramsResult = new URLSearchParams({
      SearchText: searchText
    });
  }

  if (pageNumber && pageSize && !searchText) {
    paramsResult = new URLSearchParams({
      PageNumber: String(pageNumber),
      PageSize: String(pageSize)
    });
  }

  if (pageNumber && searchText && !pageSize) {
    paramsResult = new URLSearchParams({
      PageNumber: String(pageNumber),
      SearchText: searchText
    });
  }

  if (pageSize && searchText && !pageNumber) {
    paramsResult = new URLSearchParams({
      PageSize: String(pageSize),
      SearchText: searchText
    });
  }

  if (pageNumber && pageSize && searchText) {
    paramsResult = new URLSearchParams({
      PageNumber: String(pageNumber),
      PageSize: String(pageSize),
      SearchText: searchText
    });
  }

  if (filters?.ignoreVisitConsumption) {
    paramsResult.append('IgnoreVisitConsumption', String(filters.ignoreVisitConsumption));
  }

  if (filters?.SelectedContractConcepts && filters.SelectedContractConcepts.length > 0) {
    filters.SelectedContractConcepts.forEach((concept: ContractConcept) => {
      paramsResult.append('Concepts', String(concept.id));
    });
  }

  if (filters?.SelectedCurrencys) {
    paramsResult.append('Currencies', filters.SelectedCurrencys);
  }

  if (filters?.SelectedProtocolPatients && filters.SelectedProtocolPatients.length > 0) {
    filters.SelectedProtocolPatients.forEach((patient: ProtocolPatient) => {
      paramsResult.append('Patients', String(patient.numberInProtocol));
    });
  }

  if (filters?.SelectedPractitioners && filters.SelectedPractitioners.length > 0) {
    filters.SelectedPractitioners.forEach((practitioner: Practitioner) => {
      paramsResult.append('Practitioners', practitioner.userId);
    });
  }

  if (filters?.SelectedContractStages && filters.SelectedContractStages.length > 0) {
    filters.SelectedContractStages.forEach((stageName: ContractStage) => {
      paramsResult.append('StagesName', stageName.stageName);
    });
  }

  if (filters?.SelectedStatus && filters.SelectedStatus.length > 0) {
    filters.SelectedStatus.forEach((status: IConsumptionStatusProps) => {
      paramsResult.append('Statuses', String(status.id));
    });
  }

  if (filters?.SelectedProtocols && filters.SelectedProtocols.length > 0) {
    filters.SelectedProtocols.forEach((protocol: Protocol) => {
      paramsResult.append('Protocols', String(protocol.id));
    });
  }

  if (filters?.SelectedSettlements && filters.SelectedSettlements.length > 0) {
    filters.SelectedSettlements.forEach((settlement) => {
      paramsResult.append('Settlements', String(settlement.id));
    });
  }

  if (filters?.showGroupedByVisit) {
    paramsResult.append('ShowGroupedByVisit', String(filters.showGroupedByVisit));
  }

  if (filters?.GroupId) {
    paramsResult.append('GroupId', String(filters.GroupId));
  }

  if (filters?.SelectedDateRange && filters.SelectedDateRange.length > 0) {
    if (filters.SelectedDateRange[0]) {
      paramsResult.append(
        'DateFrom',
        (filters.SelectedDateRange[0] as Date).toISOString()
      );
    }
    if (filters.SelectedDateRange[1]) {
      paramsResult.append(
        'DateTo',
        (filters.SelectedDateRange[1] as Date).toISOString()
      );
    }
  }

  return paramsResult;
};

// process http error
export const HttpErrorHandler = (
  body: string | null,
  response: any
): ErrorType => {
  let CurrentResponse: ErrorType;
  if (body) {
    let ErrorBody: any;

    try {
      ErrorBody = JSON.parse(body);
    } catch {
      CurrentResponse = {
        ErrorCode: response.status,
        ErrorMessage: String(body).trim() || response.statusText || 'error inesperado',
        Errors: []
      };

      return ErrorDefinitionResponse(CurrentResponse);
    }

    // Normalize various error payload shapes (ProblemDetails, custom, etc.)
    const normalizedErrors = (() => {
      const raw = ErrorBody?.errors ?? ErrorBody?.Errors ?? ErrorBody?.errorDescriptions;
      if (!raw) return [] as string[];
      if (Array.isArray(raw)) {
        // Already an array (ensure string[])
        return raw.map((e) => String(e));
      }
      if (typeof raw === 'object') {
        // ProblemDetails model state: { field: ["msg1", "msg2"], ... }
        const acc: string[] = [];
        Object.values(raw).forEach((v: any) => {
          if (Array.isArray(v)) acc.push(...v.map((x) => String(x)));
          else if (v != null) acc.push(String(v));
        });
        return acc;
      }
      // String or other primitive
      return [String(raw)];
    })();

    // Prefer specific message fields, then detail/title, then joined errors, else fallback
    const preferredMessage =
      ErrorBody?.message ||
      ErrorBody?.Message ||
      ErrorBody?.detail ||
      ErrorBody?.Detail ||
      ErrorBody?.title ||
      (normalizedErrors.length > 0 ? normalizedErrors[0] : undefined) ||
      'error inesperado';

    CurrentResponse = {
      ErrorCode: ErrorBody.statusCode || ErrorBody.status || response.status,
      ErrorMessage: String(preferredMessage),
      Errors: normalizedErrors
    };

    return ErrorDefinitionResponse(CurrentResponse);
  } else {
    CurrentResponse = {
      ErrorCode: response.status,
      ErrorMessage: response.statusText,
      Errors: response.errors || []
    };

    return ErrorDefinitionResponse(CurrentResponse);
  }
};

const ErrorDefinitionResponse = (error: ErrorType): ErrorType => {
  switch (error.ErrorCode) {
    case 401:
      error.ErrorMessage = 'No Autorizado';
      break;
    case 404:
      error.ErrorMessage = 'Servidor fuera de linea o Recurso no encontrado';
      break;
    case 405:
      error.ErrorMessage = 'Metodo no encontrado al peticionar el recurso';
      break;
    case 409:
    case 400:
      // For validation/business errors, surface the first detailed error if available
      if (!error.ErrorMessage || /^\s*$/.test(error.ErrorMessage)) {
        error.ErrorMessage =
          error.Errors && error.Errors.length > 0
            ? error.Errors[0]
            : 'Error al procesar la solicitud, verifique la informacion suministrada';
      }
      break;
    case 500:
      error.ErrorMessage =
        error.ErrorMessage ? error.ErrorMessage : 'Ocurrio un error inesperado al consultar por el recurso';
      break;
    default:
      return error;
  }

  return error;
};

export const DynamicSort = (property: any) => {
  let sortOrder = 1;
  if (property[0] === '-') {
    sortOrder = -1;
    property = property.substr(1);
  }
  return function (a: any, b: any) {
    if ((a === null || a === undefined) || (
      b === null || b === undefined
    ) ) return 0;
    const result =
      a[property]?.toString().toUpperCase() <
      b[property]?.toString().toUpperCase()
        ? -1
        : a[property] > b[property]
        ? 1
        : 0;
    return result * sortOrder;
  };
};

export const DynamicSortNumeric = (property: any) => {
  let sortOrder = 1;
  if (property[0] === '-') {
    sortOrder = -1;
    property = property.substr(1);
  }
  return function (a: any, b: any) {
    const result =
      a[property] < b[property]
        ? -1
        : a[property] > b[property]
        ? 1
        : 0;
    return result * sortOrder;
  };
}

export function getNameInitials(name: string): string {
  const names = name.split(' ');

  var Initials = '';

  var Name1 = names[0].charAt(0);
  var Name2 = names[names.length > 1 ? 1 : 0 ].charAt(0);

  if (Name1 === Name2) {
    Initials = Name1.toUpperCase();
  } else {
    Initials = Name1.toUpperCase() +
    Name2.toUpperCase()
  }

  return (
    Initials
  );
}

export async function ProcessError(error: any) {
  if (error.text) {
    return await error
      .text()
      .then((body: any) => HttpErrorHandler(body, error));
  } else return HttpErrorHandler(null, error);
}
